import { ConfirmDialog } from './ui/ConfirmDialog';
import { useDirtyDismiss } from './ui/useDirtyDismiss';
import { Dialog } from './ui/Dialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, X, Save, Package, AlertTriangle, Trash2, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import apiClient from '../api/client';

interface InventoryItem {
  _id: string;
  name: string;
  unit: string;
  stock: number;
  minStock: number;
  category: string;
  revision?: number;
}

const categories = [
  { value: 'panes', label: 'Panes' },
  { value: 'embutidos', label: 'Embutidos' },
  { value: 'bebidas', label: 'Bebidas' },
  { value: 'salsas', label: 'Salsas' },
  { value: 'otros', label: 'Otros' }
];
const stockStatus = (item: InventoryItem) => item.stock <= 0 ? 'out' : item.stock < item.minStock ? 'low' : 'available';
const normalizeSearch = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es').trim();

const InventoryManager = () => {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const initialForm = useRef('');
  const writeLock = useRef(false);
  const [actionResult, setActionResult] = useState('');
  const resultRef = useRef<HTMLParagraphElement>(null);
  const [saveError, setSaveError] = useState('');
  const saveErrorRef = useRef<HTMLParagraphElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  useEffect(() => { if (saveError) saveErrorRef.current?.focus(); }, [saveError]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState<{ name: string; unit: string; stock: number; minStock: number; category: string; revision?: number }>({
    name: '',
    unit: 'unidades',
    stock: 0,
    minStock: 10,
    category: 'otros'
  });

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await apiClient.get('/inventory');
      setInventory(res.data);
    } catch (error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const beginEditing = (data: typeof formData) => { initialForm.current = JSON.stringify(data); setFormData(data); };

  const handleOpenModal = (item: InventoryItem | null = null) => {
    setSaveError('');
    setActionResult('');
    if (item) {
      setEditingItem(item);
      beginEditing({ ...item });
    } else {
      setEditingItem(null);
      beginEditing({
        name: '',
        unit: 'unidades',
        stock: 0,
        minStock: 10,
        category: 'otros'
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (writeLock.current) return;
    writeLock.current = true;
    setSaving(true);
    setSaveError('');
    try {
      if (editingItem) {
        await apiClient.put(`/inventory/${editingItem._id}`, { ...formData, expectedRevision: formData.revision });
        showToast('Insumo actualizado', 'success');
      } else {
        await apiClient.post('/inventory', formData);
        showToast('Insumo creado', 'success');
      }
      setActionResult('Insumo guardado: ' + formData.name);
      setIsModalOpen(false);
      fetchInventory();
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Error al guardar insumo. Tus cambios se conservan; intenta de nuevo.');
    } finally {
      writeLock.current = false;
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    await apiClient.delete(`/inventory/${deleteTarget._id}`);
    setActionResult('Insumo eliminado: ' + deleteTarget.name);
    fetchInventory();
  };

  const filteredItems = inventory.filter(item =>
    normalizeSearch(item.name).includes(normalizeSearch(search)) &&
    (category === 'all' || item.category === category) &&
    (status === 'all' || stockStatus(item) === status)
  );
  const groups = categories.map(group => ({
    ...group,
    items: filteredItems.filter(item => item.category === group.value)
      .sort((a, b) => a.name.localeCompare(b.name, 'es'))
  })).filter(group => group.items.length > 0);
  const resetFilters = () => { setSearch(''); setCategory('all'); setStatus('all'); };
  const hasFilters = search !== '' || category !== 'all' || status !== 'all';
  const ready = !loading && !loadError;

  useEffect(() => { if (actionResult && !isModalOpen && !deleteTarget) resultRef.current?.focus(); }, [actionResult, isModalOpen, deleteTarget]);

  const { requestClose, discardConfirmation } = useDirtyDismiss({
    dirty: isModalOpen && JSON.stringify(formData) !== initialForm.current, busy: saving,
    onDiscard: () => { setIsModalOpen(false);  }
  });

  return (
    <div className="space-y-6">
      {actionResult && <p role="status" ref={resultRef} tabIndex={-1} className="rounded-xl bg-green-50 dark:bg-green-900/20 p-3">{actionResult}</p>}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black flex items-center gap-3">
            <Package className="text-primary shrink-0" aria-hidden="true" /> Inventario
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Revisa tus insumos por categoría y detecta qué necesitas reponer.</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="shrink-0 gap-2 self-start">
          <Plus size={20} aria-hidden="true" /> Nuevo insumo
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" aria-label="Resumen del inventario">
        {[
          { label: 'Insumos registrados', count: inventory.length, hint: 'En todas las categorías', color: 'text-slate-900 dark:text-white' },
          { label: 'Stock bajo', count: inventory.filter(item => stockStatus(item) === 'low').length, hint: 'Por debajo del mínimo', color: 'text-amber-800 dark:text-amber-300' },
          { label: 'Agotados', count: inventory.filter(item => stockStatus(item) === 'out').length, hint: 'Sin existencias', color: 'text-red-700 dark:text-red-300' }
        ].map(metric => (
          <Card key={metric.label} className="!p-5">
            <p className="text-sm font-bold text-slate-600 dark:text-slate-400">{metric.label}</p>
            <p className={'mt-1 text-3xl font-black ' + metric.color}>{ready ? metric.count : '—'}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{metric.hint}</p>
          </Card>
        ))}
      </div>

      <Card className="!p-4 sm:!p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="inventory-search" className="block text-sm font-bold mb-2">Buscar insumo</label>
            <div className="relative">
              <Search size={18} className="absolute left-3 top-3.5 text-slate-500" aria-hidden="true" />
              <input id="inventory-search" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Ej: pan, chorizo…" className="w-full min-h-11 pl-10 pr-3 rounded-xl bg-slate-100 dark:bg-slate-900 focus:ring-2 focus:ring-primary" />
            </div>
          </div>
          <div>
            <label htmlFor="inventory-category-filter" className="block text-sm font-bold mb-2">Categoría</label>
            <select id="inventory-category-filter" value={category} onChange={e => setCategory(e.target.value)} className="w-full min-h-11 px-3 rounded-xl bg-slate-100 dark:bg-slate-900 focus:ring-2 focus:ring-primary">
              <option value="all">Todas las categorías</option>
              {categories.map(group => <option key={group.value} value={group.value}>{group.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="inventory-status-filter" className="block text-sm font-bold mb-2">Estado del stock</label>
            <select id="inventory-status-filter" value={status} onChange={e => setStatus(e.target.value)} className="w-full min-h-11 px-3 rounded-xl bg-slate-100 dark:bg-slate-900 focus:ring-2 focus:ring-primary">
              <option value="all">Todos los estados</option>
              <option value="low">Stock bajo</option>
              <option value="out">Agotado</option>
              <option value="available">Disponible</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
          <p role="status" className="text-sm text-slate-600 dark:text-slate-400">{ready ? filteredItems.length + ' de ' + inventory.length + ' insumos' : loading ? 'Cargando inventario…' : 'Inventario no disponible'}</p>
          {hasFilters && <Button variant="ghost" onClick={resetFilters} className="text-sm">Limpiar filtros</Button>}
        </div>
      </Card>

      {loading ? (
        <p className="py-10 text-center text-slate-600 dark:text-slate-400">Cargando insumos…</p>
      ) : loadError ? (
        <div role="alert" className="rounded-2xl border border-red-200 dark:border-red-900 p-6 text-center space-y-3">
          <p className="font-bold">No se pudo cargar el inventario.</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">Revisa la conexión e inténtalo de nuevo.</p>
          <Button variant="outline" onClick={fetchInventory}>Reintentar</Button>
        </div>
      ) : inventory.length === 0 ? (
        <Card className="!p-8 text-center space-y-3">
          <Package size={32} className="mx-auto text-slate-400" aria-hidden="true" />
          <h3 className="text-xl font-bold">Tu inventario empieza aquí</h3>
          <p className="text-slate-600 dark:text-slate-400">Agrega tu primer insumo con su categoría, cantidad y stock mínimo.</p>
          <Button onClick={() => handleOpenModal()}>Crear primer insumo</Button>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card className="!p-8 text-center space-y-3">
          <h3 className="text-xl font-bold">No encontramos insumos</h3>
          <p className="text-slate-600 dark:text-slate-400">Prueba otro nombre o limpia los filtros.</p>
          <Button variant="outline" onClick={resetFilters}>Limpiar filtros</Button>
        </Card>
      ) : groups.map(group => (
        <section key={group.value} aria-labelledby={'category-' + group.value} className="space-y-3">
          <div className="flex items-center gap-3">
            <h3 id={'category-' + group.value} className="text-lg font-black">{group.label}</h3>
            <span className="rounded-full bg-slate-200 dark:bg-slate-800 px-2.5 py-1 text-xs font-bold">{group.items.length} {group.items.length === 1 ? 'insumo' : 'insumos'}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {group.items.map(item => {
              const itemStatus = stockStatus(item);
              const statusLabel = itemStatus === 'out' ? 'Agotado' : itemStatus === 'low' ? 'Stock bajo' : 'Disponible';
              const statusColor = itemStatus === 'out' ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300' : itemStatus === 'low' ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300' : 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300';
              return (
                <Card key={item._id} className="!p-5 min-w-0">
                  <div className="flex flex-wrap justify-between items-start gap-2">
                    <h4 className="text-lg font-bold break-words min-w-0 flex-1">{item.name}</h4>
                    <span className={'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ' + statusColor}>
                      {itemStatus !== 'available' && <AlertTriangle size={13} aria-hidden="true" />}{statusLabel}
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 gap-3 my-5">
                    <div><dt className="text-xs text-slate-600 dark:text-slate-400">Stock actual</dt><dd className="mt-1"><span className="text-2xl font-black">{item.stock.toLocaleString('es-CO')}</span> <span className="text-sm text-slate-600 dark:text-slate-400">{item.unit}</span></dd></div>
                    <div className="border-l border-slate-200 dark:border-slate-700 pl-4"><dt className="text-xs text-slate-600 dark:text-slate-400">Mínimo</dt><dd className="mt-1"><span className="text-2xl font-black">{item.minStock.toLocaleString('es-CO')}</span> <span className="text-sm text-slate-600 dark:text-slate-400">{item.unit}</span></dd></div>
                  </dl>
                  <div className="flex flex-wrap gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
                    <Button variant="outline" onClick={() => handleOpenModal(item)} aria-label={'Editar ' + item.name} className="gap-2 !px-3 text-sm"><Edit2 size={15} aria-hidden="true" />Editar</Button>
                    <Button variant="ghost" onClick={() => { setActionResult(''); setDeleteTarget(item); }} aria-label={'Eliminar ' + item.name} className="gap-2 !px-3 text-sm text-red-700 dark:text-red-300"><Trash2 size={15} aria-hidden="true" />Eliminar</Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      ))}

      {/* Modal Form */}
      <AnimatePresence>
        {isModalOpen && (
          <Dialog labelledBy="inventory-dialog" onClose={requestClose} busy={saving} className="flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={requestClose}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative bg-white dark:bg-slate-950 w-full max-w-md max-h-[calc(100dvh-2rem)] rounded-[32px] shadow-2xl overflow-y-auto"
            >
              <form onSubmit={handleSubmit} className="p-5 sm:p-8">
                {saveError && <p ref={saveErrorRef} role="alert" tabIndex={-1} className="mb-4 text-red-700 dark:text-red-300">{saveError}</p>}
                <fieldset disabled={saving} className="contents">
                <div className="flex justify-between items-center mb-8">
                  <h2 id="inventory-dialog" tabIndex={-1} className="text-3xl font-black">
                    {editingItem ? 'Editar Insumo' : 'Nuevo Insumo'}
                  </h2>
                  <Button type="button" variant="ghost" size="icon" aria-label="Cerrar editor de inventario" onClick={requestClose}>
                    <X size={24} />
                  </Button>
                </div>

                <div className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="inventory-name" className="text-sm font-bold text-slate-500 uppercase">Nombre del Insumo (obligatorio)</label>
                    <input 
                      id="inventory-name"
                      required
                      placeholder="Ej: Pan Francés, Chorizo..."
                      className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="inventory-stock" className="text-sm font-bold text-slate-500 uppercase">Stock Actual (obligatorio)</label>
                      <input 
                        id="inventory-stock"
                        required
                        type="number"
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.stock}
                        onChange={e => setFormData({...formData, stock: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="inventory-min-stock" className="text-sm font-bold text-slate-500 uppercase">Stock Mínimo (obligatorio)</label>
                      <input 
                        id="inventory-min-stock"
                        required
                        type="number"
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.minStock}
                        onChange={e => setFormData({...formData, minStock: parseFloat(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="inventory-unit" className="text-sm font-bold text-slate-500 uppercase">Unidad (obligatoria)</label>
                      <select 
                        id="inventory-unit"
                        required
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.unit}
                        onChange={e => setFormData({...formData, unit: e.target.value})}
                      >
                        <option value="unidades">Unidades</option>
                        <option value="gramos">Gramos (g)</option>
                        <option value="kilogramos">Kilogramos (kg)</option>
                        <option value="ml">Mililitros (ml)</option>
                        <option value="litros">Litros (L)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="inventory-category" className="text-sm font-bold text-slate-500 uppercase">Categoría (obligatoria)</label>
                      <select 
                        id="inventory-category"
                        required
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.category}
                        onChange={e => setFormData({...formData, category: e.target.value})}
                      >
                        {categories.map(group => <option key={group.value} value={group.value}>{group.label}</option>)}
                      </select>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-16 rounded-2xl text-lg font-black mt-4 flex items-center justify-center gap-2 shadow-lg shadow-primary/20">
                    <Save size={20} />
                    {editingItem ? 'Actualizar Stock' : 'Crear Insumo'}
                  </Button>
                </div>
                </fieldset>
              </form>
            </motion.div>
          </Dialog>
        )}
      </AnimatePresence>
      {discardConfirmation}
      {deleteTarget && <ConfirmDialog title={'¿Eliminar el insumo "' + deleteTarget.name + '"?'} confirmLabel="Eliminar insumo" onConfirm={confirmDelete} onClose={() => setDeleteTarget(null)}>
        <p>Esta acción no se puede deshacer. El insumo se elimina definitivamente; el servidor impedirá eliminarlo si está usado en recetas o pedidos.</p>
      </ConfirmDialog>}
    </div>
  );
};

export default InventoryManager;
